import React, {
	useRef,
	useEffect,
	useState,
} from "react";

import {
	gsap,
} from "gsap";

import {
	Expo,
} from "gsap/all";

import * as Complications from "../../data/complications";

import {
	isTouchDevice,
} from "../../hooks/utils";

import {
	isTablet,
} from "../../hooks/isMobile";

import useOnScreen from "../../hooks/useOnScreen";

import {
	useEffectOnce,
	destroyVideo,
	destroyEl,
} from "../../hooks/useEffectOnce";

import CaseFilterIdentity from "../CaseFilterIdentity";

import MarqueeTitle from "../Elements/MarqueeTitle";

import SlantedTag from "../Elements/SlantedTag";

import {
	getCaseDisplayFilter,
} from "../../data/cases/caseFilterPresentation";

import styles from "./SolutionSlide.module.css";


/*
 * ============================================================
 * MOBILE PREVIEW FORMAT
 * ============================================================
 *
 * Existing cases:
 *
 * undefined
 * ""
 * "legacy"
 *
 * → keep the existing three-way / split-video behavior.
 *
 *
 * New cases:
 *
 * "9:16"
 * "4:5"
 *
 * → use the video as a native mobile composition.
 *
 *
 * A couple of equivalent Sheet-friendly spellings are accepted
 * as well:
 *
 * 9x16
 * 9/16
 *
 * 4x5
 * 4/5
 */

const normalizeMobilePreviewFormat =
	(value) => {
		const normalizedValue =
			String(
				value ??
					"",
			)
				.trim()
				.toLowerCase()
				.replace(
					/\s+/g,
					"",
				);


		switch (
			normalizedValue
		) {
			case "9:16":
			case "9x16":
			case "9/16":
				return "9:16";


			case "4:5":
			case "4x5":
			case "4/5":
				return "4:5";


			default:
				return null;
		}
	};


const SolutionSlide = React.forwardRef(
	(props, ref) => {
		const slideRef =
			useRef(null);

		const isSlideOnScreen =
			useOnScreen(
				slideRef,
			);

		const complicationsRef =
			useRef(null);

		const activeSlideTimer =
			useRef(null);

		const contentRef =
			useRef(null);

		const sliderContent =
			useRef(null);

		const [
			srcVidMain,
			setSrcVidMain,
		] = useState(
			undefined,
		);

		const [
			srcVidPreview,
			setSrcVidPreview,
		] = useState(
			undefined,
		);

		const [
			isPortraitMode,
			setIsPortraitMode,
		] = useState(
			false,
		);

		const [
			isEnabled,
			setIsEnabled,
		] = useState(
			false,
		);

		const [
			titleHovered,
			setTitleHovered,
		] = useState(
			false,
		);

		const isTouch =
			isTouchDevice();


		/*
		 * ========================================================
		 * MOBILE MEDIA FORMAT
		 * ========================================================
		 *
		 * IMPORTANT:
		 *
		 * The format affects ONLY portrait/mobile mode.
		 *
		 *
		 * Legacy case:
		 *
		 * mobilePreviewFormat missing
		 *
		 * → existing split video
		 * → existing panVideo thirds
		 * → existing CSS
		 *
		 *
		 * Native mobile case:
		 *
		 * mobilePreviewFormat = "9:16"
		 *
		 * or
		 *
		 * mobilePreviewFormat = "4:5"
		 *
		 * → no split-video crop
		 * → no thirds positioning
		 * → media fills the mobile viewport using object-fit
		 */

		const mobilePreviewFormat =
			normalizeMobilePreviewFormat(
				props.solution
					?.mobilePreviewFormat,
			);


		const usesCustomMobilePreview =
			Boolean(
				isPortraitMode &&
					mobilePreviewFormat,
			);


		const customMobileFormatClass =
			mobilePreviewFormat ===
			"9:16"
				? styles.mobileNineSixteen
				: mobilePreviewFormat ===
				  "4:5"
					? styles.mobileFourFive
					: "";


		const nativeMobileMediaClass =
			usesCustomMobilePreview
				? ` ${styles.nativeMobileMedia}${
						customMobileFormatClass
							? ` ${customMobileFormatClass}`
							: ""
				  }`
				: "";


		const nativeMobilePosterClass =
			usesCustomMobilePreview
				? ` ${styles.nativeMobilePoster}${
						customMobileFormatClass
							? ` ${customMobileFormatClass}`
							: ""
				  }`
				: "";


		/*
		 * 0 preview
		 * 1 split/main
		 */
		const [
			curMode,
			setCurMode,
		] = useState(
			0,
		);

		const vid =
			useRef(null);

		const vidPreview =
			useRef(null);

		const poster =
			useRef(null);

		const videoIsStopped =
			useRef(true);

		const activeVideo =
			useRef(
				props.activeVideo,
			);

		const activeSlideRef =
			useRef(
				props.activeSlide,
			);


		/*
		 * ========================================================
		 * DISPLAY TOPIC
		 * ========================================================
		 *
		 * This is ONLY the Sheet-driven topic filter.
		 *
		 * Work / Solution remains a separate category pill below.
		 */

		const displayFilter =
			getCaseDisplayFilter(
				props.solution,
				props.caseFilter,
			);

		const displayFilterIsPrimary =
			displayFilter?.value ===
				"work" ||
			displayFilter?.value ===
				"solution";

		const hasTopicFilter =
			Boolean(
				displayFilter?.value,
			) &&
			!displayFilterIsPrimary;


		const isActiveSlide =
			() => {
				return (
					props.activeSlide ===
					props.index
				);
			};


		/*
		 * ========================================================
		 * VIDEO POSITION
		 * ========================================================
		 */

		const centerMainVideo =
			() => {
				if (
					!vid.current
				) {
					return;
				}


				const transform =
					"translate(-50%, -50%)";


				vid.current.style.transform =
					transform;

				vid.current.style.webkitTransform =
					transform;
			};


		const handlePlayVideo =
			() => {
				let videoToPlay =
					undefined;

				videoIsStopped.current =
					false;

				if (
					curMode ===
						0
				) {
					videoToPlay =
						vidPreview;
				} else {
					videoToPlay =
						vid;
				}

				if (
					typeof videoToPlay?.current !==
						"undefined"
				) {
					handlePlayVideoRef(
						videoToPlay,
					);
				}
			};


		const handlePlayVideoRef =
			(videoRef) => {
				const playPromise =
					videoRef.current?.play?.();

				if (
					playPromise !==
						undefined
				) {
					playPromise
						.then(
							() => {},
						)
						.catch(
							() => {},
						);
				}
			};


		const handleStopVideo =
			() => {
				if (
					!isPortraitMode
				) {
					setCurMode(
						0,
					);
				}

				if (
					!videoIsStopped.current
				) {
					videoIsStopped.current =
						true;

					if (
						vid.current
					) {
						vid.current.pause();
					}

					if (
						vidPreview.current
					) {
						vidPreview.current.pause();
					}
				}
			};


		/*
		 * ========================================================
		 * VIDEO PAN
		 * ========================================================
		 *
		 * Legacy portrait media:
		 *
		 * The main video contains several compositions and is
		 * moved horizontally so only the desired third appears.
		 *
		 *
		 * Native 9:16 / 4:5 media:
		 *
		 * The media itself is already authored for mobile.
		 *
		 * Therefore no thirds positioning must happen.
		 */

		const panVideo =
			() => {
				if (
					!vid?.current
				) {
					return;
				}

				if (
					props.debugComplication >
						-1
				) {
					activeVideo.current =
						props.debugComplication;
				}


				complicationsRef.current?.switchVideo?.(
					activeVideo.current,
				);


				/*
				 * ====================================================
				 * NATIVE MOBILE FORMAT
				 * ====================================================
				 *
				 * Do not execute any legacy thirds positioning.
				 */

				if (
					usesCustomMobilePreview
				) {
					centerMainVideo();

					return;
				}


				/*
				 * ====================================================
				 * LEGACY PORTRAIT
				 * ====================================================
				 */

				if (
					isPortraitMode
				) {
					switch (
						activeVideo.current
					) {
						case 0: {
							vid.current.style.webkitTransform =
								`translateX(${
									-50 /
									3
								}%) translateY(${-50}%)`;

							break;
						}


						case 1: {
							vid.current.style.webkitTransform =
								`translateX(${-50}%) translateY(${-50}%)`;

							break;
						}


						case -1:
						case 2: {
							vid.current.style.webkitTransform =
								`translateX(${
									-1 *
									(
										25 /
											3 +
										75
									)
								}%) translateY(${-50}%)`;

							break;
						}


						default: {
							vid.current.style.webkitTransform =
								`translateX(${-50}%) translateY(${-50}%)`;

							break;
						}
					}
				} else {
					/*
					 * =================================================
					 * LEGACY LANDSCAPE
					 * =================================================
					 */

					switch (
						activeVideo.current
					) {
						default:
						case 0: {
							vid.current.style.webkitTransform =
								`translateX(${-50}%) translateY(${
									(
										-(
											1 /
											2
										) +
										1 /
											3
									) *
									100
								}%) `;

							break;
						}


						case 1: {
							vid.current.style.webkitTransform =
								`translateX(${-50}%) translateY(${
									-(
										1 /
										2
									) *
									100
								}%)`;

							break;
						}


						case -1:
						case 2: {
							vid.current.style.webkitTransform =
								`translateX(${-50}%) translateY(${
									(
										-(
											1 /
											2
										) -
										1 /
											3
									) *
									100
								}%) `;

							break;
						}


						case 3: {
							vid.current.style.webkitTransform =
								`translateX(${-75}%) translateY(${-75}%)`;

							break;
						}
					}


					if (
						activeVideo.current !==
							2 &&
						activeVideo.current !==
							-1 &&
						curMode ===
							0
					) {
						setCurMode(
							1,
						);
					}
				}
			};


		/*
		 * ========================================================
		 * PRELOAD / APPEAR
		 * ========================================================
		 */

		useEffect(() => {
			if (
				props.preloaded
			) {
				gsap.killTweensOf(
					slideRef.current,
				);

				gsap.killTweensOf(
					poster.current,
				);

				gsap.to(
					slideRef.current,
					{
						duration:
							0.3,

						autoAlpha:
							1,
					},
				);

				gsap.fromTo(
					contentRef.current,
					0.66,
					{
						autoAlpha:
							0,

						xPercent:
							100,
					},
					{
						delay:
							0.33,

						xPercent:
							0,

						autoAlpha:
							1,

						ease:
							Expo.easeOut,
					},
				);

				gsap.fromTo(
					poster.current,
					0.66,
					{
						autoAlpha:
							0,

						xPercent:
							100,
					},
					{
						autoAlpha:
							1,

						xPercent:
							0,

						ease:
							Expo.easeOut,

						force3D:
							true,

						delay:
							0.5,

						onComplete:
							() => {
								if (
									vid.current &&
									vidPreview.current
								) {
									gsap.set(
										[
											vid.current,
											vidPreview.current,
										],
										{
											visibility:
												"visible",
										},
									);
								}
							},
					},
				);
			}
		}, [
			props.preloaded,
		]);


		/*
		 * ========================================================
		 * ACTIVE VIDEO
		 * ========================================================
		 */

		useEffect(() => {
			if (
				isActiveSlide() ||
				props.activeVideo ===
					-1
			) {
				if (
					activeVideo.current !==
						props.activeVideo
				) {
					activeVideo.current =
						props.activeVideo;

					panVideo();
				}
			}
		}, [
			props.activeVideo,
		]);


		/*
		 * ========================================================
		 * ENABLED
		 * ========================================================
		 */

		useEffect(() => {
			if (
				props.isEnabled !==
					isEnabled
			) {
				setIsEnabled(
					props.isEnabled,
				);
			}
		}, [
			props.isEnabled,
		]);


		/*
		 * ========================================================
		 * VIDEO SOURCES / ORIENTATION
		 * ========================================================
		 */

		useEffect(() => {
			if (
				props.main !==
					srcVidMain
			) {
				setSrcVidPreview(
					props.preview,
				);

				setSrcVidMain(
					props.main,
				);
			}


			if (
				props.isPortraitMode !==
					isPortraitMode
			) {
				setIsPortraitMode(
					props.isPortraitMode,
				);


				panVideo();


				if (
					props.isPortraitMode
				) {
					/*
					 * Portrait mode always uses the main video.
					 *
					 * Legacy:
					 * main = multi-video / split source
					 *
					 * Custom:
					 * main = native 9:16 / 4:5 source
					 */

					setCurMode(
						1,
					);
				} else {
					setCurMode(
						0,
					);
				}
			}
		}, [
			props.main,
			props.isPortraitMode,
		]);


		/*
		 * ========================================================
		 * RE-CENTER NATIVE MOBILE MEDIA
		 * ========================================================
		 *
		 * This runs after React has committed the portrait-mode
		 * state, avoiding any stale transform left over from the
		 * previous legacy mode.
		 */

		useEffect(() => {
			if (
				usesCustomMobilePreview
			) {
				centerMainVideo();
			}
		}, [
			usesCustomMobilePreview,
			mobilePreviewFormat,
		]);


		/*
		 * ========================================================
		 * POSTER FADE
		 * ========================================================
		 */

		useEffect(() => {
			const _vidCurrent =
				vid.current;

			const _vidCurrentPreview =
				vidPreview.current;

			if (
				!poster.current
			) {
				return;
			}


			const fadeOutPosterMainVid =
				() => {
					if (
						!_vidCurrent
					) {
						return;
					}

					if (
						_vidCurrent.currentTime >
							0
					) {
						panVideo();

						gsap.to(
							poster.current,
							{
								duration:
									0,

								autoAlpha:
									0,
							},
						);
					}
				};


			const fadeOutPosterPreviewVid =
				() => {
					if (
						!_vidCurrentPreview
					) {
						return;
					}

					if (
						_vidCurrentPreview.currentTime >
							0
					) {
						panVideo();

						gsap.to(
							poster.current,
							{
								duration:
									0,

								autoAlpha:
									0,
							},
						);
					}
				};


			if (
				curMode ===
					0
			) {
				_vidCurrentPreview?.addEventListener(
					"timeupdate",
					fadeOutPosterPreviewVid,
				);
			} else {
				_vidCurrent?.addEventListener(
					"timeupdate",
					fadeOutPosterMainVid,
				);
			}


			return () => {
				if (
					_vidCurrent
				) {
					_vidCurrent.removeEventListener(
						"timeupdate",
						fadeOutPosterMainVid,
					);
				}

				if (
					_vidCurrentPreview
				) {
					_vidCurrentPreview.removeEventListener(
						"timeupdate",
						fadeOutPosterPreviewVid,
					);
				}
			};
		}, [
			poster.current,
			curMode,
		]);


		/*
		 * ========================================================
		 * INIT / DESTROY
		 * ========================================================
		 */

		useEffectOnce(() => {
			const _vidCurrent =
				vid.current;

			const _vidCurrentPreview =
				vidPreview.current;

			const _slide =
				slideRef.current;


			if (
				_slide
			) {
				gsap.set(
					_slide,
					{
						autoAlpha:
							0,
					},
				);
			}


			return () => {
				destroyVideo(
					_vidCurrent,
				);

				destroyVideo(
					_vidCurrentPreview,
				);

				destroyEl(
					_slide,
				);
			};
		});


		/*
		 * ========================================================
		 * ACTIVE SLIDE
		 * ========================================================
		 */

		useEffect(() => {
			if (
				activeSlideRef.current ===
					props.activeSlide
			) {
				return;
			}


			activeSlideRef.current =
				props.activeSlide;


			/*
			 * Desktop marquee hover should never survive a
			 * transition out of the overview.
			 */

			setTitleHovered(
				false,
			);


			if (
				isPortraitMode
			) {
				setCurMode(
					1,
				);
			}


			if (
				props.activeSlide ===
					props.index
			) {
				setActiveSlideTimer();
			} else {
				clearActiveSlideTimer();

				handleStopVideo();


				if (
					isTablet &&
					!props.isPortraitMode
				) {
					gsap.to(
						poster.current,
						{
							duration:
								0,

							autoAlpha:
								1,
						},
					);
				}
			}
		}, [
			props.activeSlide,
		]);


		const clearActiveSlideTimer =
			() => {
				if (
					activeSlideTimer.current
				) {
					clearTimeout(
						activeSlideTimer.current,
					);
				}
			};


		const setActiveSlideTimer =
			() => {
				clearActiveSlideTimer();

				activeSlideTimer.current =
					setTimeout(
						() => {
							handlePlayVideo();


							if (
								curMode ===
									0 &&
								vid.current
							) {
								handlePlayVideoRef(
									vid,
								);
							}
						},
						0,
					);
			};


		/*
		 * ========================================================
		 * HOVER
		 * ========================================================
		 */

		const handleHover =
			(bool) => {
				if (
					!sliderContent.current ||
					isTouch
				) {
					return;
				}


				const hasHover =
					sliderContent.current.classList.contains(
						"hover",
					);


				if (
					bool &&
					!hasHover
				) {
					sliderContent.current.classList.add(
						"hover",
					);
				} else if (
					!bool &&
					hasHover
				) {
					sliderContent.current.classList.remove(
						"hover",
					);
				}
			};


		const currentlyOnScreen =
			isActiveSlide() ||
			isSlideOnScreen;


		/*
		 * ========================================================
		 * TITLE VISIBILITY
		 * ========================================================
		 */

		const titleIsVisible =
			currentlyOnScreen &&
			props.preloaded;


		/*
		 * ========================================================
		 * TITLE ACTIVE STATE
		 * ========================================================
		 */

		const titleMarqueeActive =
			titleIsVisible &&
			titleHovered &&
			props.activeSlide ===
				-1;


		/*
		 * ========================================================
		 * OFF SCREEN
		 * ========================================================
		 */

		useEffect(() => {
			if (
				!isSlideOnScreen
			) {
				if (
					vidPreview.current
				) {
					vidPreview.current.pause();
				}

				if (
					vid.current
				) {
					vid.current.pause();
				}

				setTitleHovered(
					false,
				);
			}
		}, [
			currentlyOnScreen,
		]);


		/*
		 * ========================================================
		 * VIDEO CLASS NAMES
		 * ========================================================
		 */

		const mainVideoClassName =
			usesCustomMobilePreview
				? `media${nativeMobileMediaClass}`
				: `media split${
						props.isPortraitMode
							? " portrait"
							: ""
				  }`;


		const previewVideoClassName =
			usesCustomMobilePreview
				? `media${nativeMobileMediaClass}`
				: `media${
						props.isPortraitMode
							? " portrait"
							: ""
				  }`;


		return (
			<div
				ref={
					slideRef
				}
				className={`slide${
					props.isPortraitMode
						? " portrait"
						: ""
				}`}
				onClick={(e) => {
					if (
						!isEnabled
					) {
						return;
					}


					props.onclick(
						e,
					);
				}}
				onMouseMove={(e) => {
					e.preventDefault();


					if (
						!isEnabled
					) {
						return;
					}


					if (
						!props.onscroll.current &&
						!e.target.playing &&
						!isTouch
					) {
						handleHover(
							true,
						);

						handlePlayVideo();
					}
				}}
				onMouseEnter={(e) => {
					e.preventDefault();


					if (
						!isEnabled
					) {
						return;
					}


					if (
						!props.onscroll.current &&
						!e.target.playing &&
						!isTouch
					) {
						setTitleHovered(
							true,
						);

						handleHover(
							true,
						);

						handlePlayVideo();
					}
				}}
				onMouseLeave={(e) => {
					e.preventDefault();

					e.stopPropagation();


					setTitleHovered(
						false,
					);

					handleHover(
						false,
					);


					if (
						isActiveSlide()
					) {
						return;
					}


					handleStopVideo();
				}}
			>
				<div
					className="content"
					style={{
						opacity:
							0,

						visibility:
							"hidden",

						display:
							currentlyOnScreen
								? "inherit"
								: "none",
					}}
					ref={
						contentRef
					}
				>
					<div
						className={`slider-txt-cnt${
							isTouch
								? " hover"
								: ""
						}`}
						ref={
							sliderContent
						}
					>
						<div className="slider-title-cnt">
							<div className="slider-index">
								{props.index +
									1 <
								10
									? "0" +
									  (props.index +
											1)
									: props.index +
									  1}
							</div>


							<MarqueeTitle
								className="slider-desc-main"
								visible={
									titleIsVisible
								}
								active={
									titleMarqueeActive
								}
							>
								{
									props.title
								}
							</MarqueeTitle>
						</div>


						<div className="slider-title-cnt">
							{hasTopicFilter ? (
								<SlantedTag
									className="slider-tag-new"
									standardSpacing
									unslantIcons
								>
									<CaseFilterIdentity
										filter={
											displayFilter.value
										}
										slide
									/>

									<div className="slider-tag-new-txt">
										{
											displayFilter.label
										}
									</div>
								</SlantedTag>
							) : null}


							<SlantedTag
								className={`slider-tag-new ${props.type}`}
								standardSpacing
								unslantIcons
							>
								<div
									className={`div-block-45${
										props.type ===
										"work"
											? " work"
											: ""
									}`}
									aria-hidden="true"
								/>

								<div className="slider-tag-new-txt">
									{props.type ===
									"solution"
										? "Solution"
										: "Work"}
								</div>
							</SlantedTag>
						</div>
					</div>
				</div>


				<div
					key={
						srcVidPreview
					}
					className="videoContainer"
					data-mobile-preview-format={
						usesCustomMobilePreview
							? mobilePreviewFormat
							: undefined
					}
					style={{
						opacity:
							1,

						display:
							currentlyOnScreen
								? "inherit"
								: "none",
					}}
				>
					<video
						ref={
							vid
						}
						style={{
							position:
								"absolute",

							display:
								curMode ===
									0
									? "none"
									: "inherit",

							visibility:
								"hidden",
						}}
						className={
							mainVideoClassName
						}
						muted
						preload="none"
						loop
						playsInline
						draggable="false"
						src={
							srcVidMain
						}
						type="video/mp4"
					/>


					<video
						ref={
							vidPreview
						}
						style={{
							position:
								"absolute",

							opacity:
								curMode ===
									1
									? 0
									: 1,

							zIndex:
								1,

							visibility:
								"hidden",
						}}
						className={
							previewVideoClassName
						}
						muted
						preload="none"
						loop
						playsInline
						draggable="false"
						src={
							srcVidPreview
						}
						type="video/mp4"
					/>


					{
						{
							VirtualCarShoot: (
								<Complications.VirtualCarShoot
									curScale={
										props.curScale
									}
									isSmallScreen={
										props.isSmallScreen
									}
									solution={
										props.solution
									}
									isActiveSlide={
										isActiveSlide()
									}
									curVideo={
										vid
									}
									srcVidMain={
										srcVidMain
									}
									ref={
										complicationsRef
									}
								/>
							),


							Audio2Face: (
								<Complications.Audio2Face
									curScale={
										props.curScale
									}
									isSmallScreen={
										props.isSmallScreen
									}
									solution={
										props.solution
									}
									isActiveSlide={
										isActiveSlide()
									}
									curVideo={
										vid
									}
									srcVidMain={
										srcVidMain
									}
									ref={
										complicationsRef
									}
								/>
							),


							MotionCapture: (
								<Complications.MotionCapture
									curScale={
										props.curScale
									}
									isSmallScreen={
										props.isSmallScreen
									}
									solution={
										props.solution
									}
									isActiveSlide={
										isActiveSlide()
									}
									curVideo={
										vid
									}
									srcVidMain={
										srcVidMain
									}
									ref={
										complicationsRef
									}
								/>
							),


							SocialTurnaround: (
								<Complications.SocialTurnaround
									curScale={
										props.curScale
									}
									isSmallScreen={
										props.isSmallScreen
									}
									solution={
										props.solution
									}
									isActiveSlide={
										isActiveSlide()
									}
									curVideo={
										vid
									}
									srcVidMain={
										srcVidMain
									}
									ref={
										complicationsRef
									}
								/>
							),


							Fortnite: (
								<Complications.Fortnite
									curScale={
										props.curScale
									}
									isSmallScreen={
										props.isSmallScreen
									}
									solution={
										props.solution
									}
									isActiveSlide={
										isActiveSlide()
									}
									curVideo={
										vid
									}
									srcVidMain={
										srcVidMain
									}
									ref={
										complicationsRef
									}
								/>
							),


							default:
								null,
						}[
							props.solution
								?.component
						]
					}


					<div
						className={`poster${nativeMobilePosterClass}`}
						ref={
							poster
						}
						style={{
							backgroundImage:
								'url("' +
								props.poster +
								'")',

							visibility:
								"hidden",

							opacity:
								0,
						}}
					/>
				</div>
			</div>
		);
	},
);


export default React.memo(
	SolutionSlide,
);